import React, { useMemo } from "react";
import { connect } from "react-redux";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypeSupportingDataView } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import { RootState } from "@app/store/rootReducer";
import { EMPTY_AGGREGATION_KEY } from "@app/util/consts";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import CustomField from "@app/util/fieldModel/impl/CustomField";

import messages from "../../messages";
import { getEntityType, getField, getGroupData } from "../../store/selectors";
import { GroupDataRecord } from "../../type/DataRecord";
import { COLUMN_BAR_HEIGHT, PIE_VIEW_LIMIT } from "../../util/const";
import useChartConfig from "../../util/useChartConfig";

import styles from "./Groups.module.scss";

interface Props {
  entityType: EntityTypeSupportingDataView;
  field?: IField;
  groupData: GroupDataRecord[];
}

const Groups: React.FC<Props> = ({ entityType, field, groupData }) => {
  const intl = useIntl();
  const groupChartData = useMemo(
    () =>
      groupData.map((record) => ({
        name:
          record.entity === EMPTY_AGGREGATION_KEY
            ? intl.formatMessage(messages.none)
            : record.entity,
        y: record.value,
      })),
    [intl, groupData]
  );

  const usePie = groupChartData.length <= PIE_VIEW_LIMIT;

  const chartConfig = useChartConfig({
    columnChartHeight: groupChartData.length * COLUMN_BAR_HEIGHT,
    intl,
    seriesData: groupChartData,
    usePie,
  });

  const showInfo = useMemo(() => {
    return (
      field &&
      (field.hasFeature(FieldFeature.GROUP_FIELD) ||
        field.hasFeature(FieldFeature.ROUTE_FIELD) ||
        (isCustomField(field) && (field as CustomField).isMultiOptionField))
    );
  }, [field]);

  return (
    <div>
      {showInfo && (
        <TextWithInfo
          className={styles.infoBlock}
          info={intl.formatMessage(messages.totalCountMayExceedNumberInfo, { entityType })}
        >
          {intl.formatMessage(messages.totalCountMayExceedNumber)}
        </TextWithInfo>
      )}
      <div className={styles.chartContainer}>
        <HighchartsReact highcharts={Highcharts} isPureConfig options={chartConfig} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  field: getField(state),
  groupData: getGroupData(state),
});

export default connect(mapStateToProps)(Groups);
